import React, { useMemo, useState } from 'react';
import { Calendar, BookOpen, Globe, Calculator, Book, Dumbbell, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseLocalDate } from '../../../lib/common/dateUtils';

interface WeekItem {
  weekStart: string;
  weekEnd: string;
  weekNumber: number;
  isCurrentWeek: boolean;
  totalPoints: number;
  averagePointsPerDay: number;
  perfectDays: number;
  activeDays: number;
  dailyBreakdown: Array<{
    date: string;
    dayOfWeek: string;
    points: number;
    activitiesComplete: number;
    perfectDay: boolean;
    keyActivities: {
      sanskrit: boolean;
      gujarati: boolean;
      ixl: boolean;
      reading: boolean;
      yoga: boolean;
    };
    // Additional fields for flexible activity detection
    newShlokas?: number;
    shlokaMeanings?: number;
    shlokaReview?: number;
    batchReview?: string | boolean;
    gujaratiDay?: string | boolean;
    ixlExercises?: number;
    mathTables?: number;
    readingPoints?: number;
    reading?: string;
    yoga?: string | boolean;
    puzzles?: number;
    chess?: string | boolean;
  }>;
}

interface ActivityGridProps {
  allWeeks: WeekItem[];
  currentWeekIndex: number;
  onWeekChange?: (weekIndex: number) => void;
}

// Activity icons configuration
const activityIcons = {
  sanskrit: { 
    icon: BookOpen, 
    color: 'text-orange-400', 
    label: 'Sanskrit' 
  },
  language: { 
    icon: Globe, 
    color: 'text-green-400', 
    label: 'Gujarati' 
  },
  academics: { 
    icon: Calculator, 
    color: 'text-blue-400', 
    label: 'Academics' 
  },
  reading: { 
    icon: Book, 
    color: 'text-purple-400', 
    label: 'Reading' 
  },
  physical: { 
    icon: Dumbbell, 
    color: 'text-red-400', 
    label: 'Physical' 
  },
};

// Helper function to determine activity status for each day
const getActivityStatus = (day: any) => {
  const activities = {
    sanskrit: false,
    language: false,
    academics: false,
    reading: false,
    physical: false,
  };

  // Check Sanskrit activities
  if (day.newShlokas > 0 || day.shlokaMeanings > 0 || day.shlokaReview > 0 || 
      day.batchReview === 'Yes' || day.batchReview === true ||
      day.keyActivities?.sanskrit) {
    activities.sanskrit = true;
  }

  // Check Language (Gujarati)
  if (day.gujaratiDay === 'Yes' || day.gujaratiDay === true || 
      day.keyActivities?.gujarati) {
    activities.language = true;
  }

  // Check Academics (IXL, Math Tables)
  if ((day.ixlExercises && day.ixlExercises > 0) || 
      (day.mathTables && day.mathTables > 0) ||
      day.keyActivities?.ixl) {
    activities.academics = true;
  }

  // Check Reading
  if ((day.readingPoints && day.readingPoints > 0) || 
      (day.reading && day.reading !== 'None' && day.reading !== '') ||
      day.keyActivities?.reading) {
    activities.reading = true;
  }

  // Check Physical (Yoga OR other physical activities)
  if (day.yoga === 'Yes' || day.yoga === true || 
      (day.puzzles && day.puzzles > 0) || 
      day.chess === 'Yes' || day.chess === true ||
      day.keyActivities?.yoga) {
    activities.physical = true;
  }

  return activities;
};

// Helper function to format week range
const formatWeekRange = (weekStart: string, weekEnd: string): string => {
  const start = parseLocalDate(weekStart);
  const end = parseLocalDate(weekEnd);
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}`;
  } else {
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
  }
};

// Simple date utility
function isSameDate(dateString: string, today: Date): boolean {
  const dayDate = new Date(dateString);
  dayDate.setHours(0, 0, 0, 0);
  return dayDate.getTime() === today.getTime();
}

export default function ActivityGrid({ allWeeks, currentWeekIndex, onWeekChange }: ActivityGridProps) {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(currentWeekIndex);
  
  // Get the selected week data
  const selectedWeek = allWeeks[selectedWeekIndex] || allWeeks[currentWeekIndex];
  
  // Memoize today's date
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // Navigation handlers
  const handlePreviousWeek = () => {
    if (selectedWeekIndex > 0) {
      const newIndex = selectedWeekIndex - 1;
      setSelectedWeekIndex(newIndex);
      onWeekChange?.(newIndex);
    }
  };
  
  const handleNextWeek = () => {
    if (selectedWeekIndex < allWeeks.length - 1) {
      const newIndex = selectedWeekIndex + 1;
      setSelectedWeekIndex(newIndex);
      onWeekChange?.(newIndex);
    }
  };
  
  const handleCurrentWeek = () => {
    setSelectedWeekIndex(currentWeekIndex);
    onWeekChange?.(currentWeekIndex);
  }; 

  // If no weeks data available, show loading state
  if (!allWeeks || allWeeks.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="text-center text-white/60">
          Loading weekly data...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
      {/* Header with Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-blue-400" />
          <h3 className="text-xl font-bold text-white">
            Weekly Activities
          </h3>
        </div>
        
        {/* Week Navigation Controls */}
        <div className="flex items-center gap-2">
          {/* Previous Week Button */}
          <button
            onClick={handlePreviousWeek}
            disabled={selectedWeekIndex === 0}
            className={`p-2 rounded-lg transition-colors ${
              selectedWeekIndex === 0 
                ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30'
            }`}
            title="Previous Week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          {/* Current Week Button */}
          <button
            onClick={handleCurrentWeek}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedWeekIndex === currentWeekIndex
                ? 'bg-green-500/30 text-green-200 border border-green-500/50'
                : 'bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-500/30'
            }`}
          >
            Current
          </button>
          
          {/* Next Week Button */}
          <button
            onClick={handleNextWeek}
            disabled={selectedWeekIndex === allWeeks.length - 1}
            className={`p-2 rounded-lg transition-colors ${
              selectedWeekIndex === allWeeks.length - 1
                ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30'
            }`}
            title="Next Week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Week Information */}
      <div className="mb-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold text-white">Week {selectedWeek.weekNumber}</span>
            <span className="ml-2 text-white/70">
              {formatWeekRange(selectedWeek.weekStart, selectedWeek.weekEnd)}
            </span>
            {selectedWeek.isCurrentWeek && (
              <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-300 text-xs rounded-full border border-green-500/30">
                Current Week
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-white">{selectedWeek.totalPoints} pts</div>
            <div className="text-sm text-white/60">
              Avg: {selectedWeek.averagePointsPerDay.toFixed(1)}/day
            </div>
          </div>
        </div>
        
        {/* Week Summary Stats */}
        <div className="mt-3 flex gap-4 text-sm">
          <div className="text-white/70">
            <span className="text-green-400">{selectedWeek.perfectDays}</span> perfect days
          </div>
          <div className="text-white/70">
            <span className="text-blue-400">{selectedWeek.activeDays}</span> active days
          </div>
        </div>
      </div>

      {/* Activity Legend */}
      <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/10">
        <div className="text-xs text-white/60 mb-2">Legend:</div>
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1">
            <span>⭐</span>
            <span className="text-white/70">Excellent (15+ pts)</span>
          </div>
          <div className="flex items-center gap-1">
            <span>✅</span>
            <span className="text-white/70">Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <span>🔸</span>
            <span className="text-white/70">Some points</span>
          </div>
          <div className="flex items-center gap-1">
            <span>❌</span>
            <span className="text-white/70">Missed</span>
          </div>
        </div>
      </div>      

      {/* Main Activity Grid */}
      <div className="space-y-2">
        {/* Grid Header */}
        <div className="grid grid-cols-8 gap-2 mb-2">
          <div className="text-xs font-medium text-white/60"></div>
          {selectedWeek.dailyBreakdown.map((day, index) => {
            const isToday = parseLocalDate(day.date).toDateString() === today.toDateString();
            const dayDate = parseLocalDate(day.date);
            return (
              <div key={day.date} className={`text-center ${isToday ? 'ring-4 ring-yellow-400 ring-offset-2 rounded-lg bg-white/10' : ''}`}> 
                <div className={`text-xs ${isToday ? 'font-bold text-yellow-500' : 'text-white/60'}`}>
                  {day.dayOfWeek}
                </div>
                <div className={`text-sm font-bold ${isToday ? 'text-yellow-500' : 'text-white'}`}
                  style={isToday ? { minWidth: 28 } : {}}>
                  {dayDate.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Activity Rows */}
        {Object.entries(activityIcons).map(([activityKey, { icon: Icon, color, label }]) => (
          <div key={activityKey} className="grid grid-cols-8 gap-2 items-center">
            <div className="text-sm text-white font-medium text-left">
              {label}
            </div>
            {selectedWeek.dailyBreakdown.map((day, dayIndex) => {
              const dayActivities = getActivityStatus(day);
              const completed = dayActivities[activityKey as keyof typeof dayActivities];
              const points = day.points;
              
              let cellClass = 'text-center p-2 rounded transition-all duration-200 hover:scale-110 cursor-pointer ';
              let cellIcon = '';
              
              if (completed && points >= 15) {
                cellClass += 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg';
                cellIcon = '⭐';
              } else if (completed) {
                cellClass += 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md';
                cellIcon = '✅';
              } else if (points > 0) {
                cellClass += 'bg-gradient-to-br from-yellow-400 to-orange-400 text-gray-800 shadow-sm';
                cellIcon = '🔸';
              } else {
                cellClass += 'bg-white/10 text-white/40 border border-white/20';
                cellIcon = '❌';
              }

              return (
                <div 
                  key={dayIndex} 
                  className={cellClass}
                  title={`${day.points} points earned${completed ? ` (${label} completed)` : ''}`}
                >
                  {cellIcon}
                </div>
              );
            })}
          </div>
        ))}

        {/* Daily Points Summary Row */}
        <div className="grid grid-cols-8 gap-2 items-center mt-4 pt-4 border-t border-white/20">
          <div className="text-sm font-bold text-white text-left">
            Points
          </div>
          {selectedWeek.dailyBreakdown.map((day, dayIndex) => (
            <div key={dayIndex} className="text-center p-2 bg-white/20 rounded font-bold text-white">
              {day.points}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}